"use client"

import Image from 'next/image'
import React, { useState } from 'react'
import HTMLRenderer from '@/components/ui/html-renderer'
import Modal from '@/components/Modal'
import { useI18n } from '@/context/i18n'

type Props = {
  title: string
  subtitle?: string
  description?: string
  image: string
  days?: number
  nights?: number
  countries?: number
  cities?: number
  priceFinal?: number
  currency?: string
  pricePrefix?: string
  priceSuffix?: string
  taxesLabel?: string
  includeFlight?: boolean
  airlineLogo?: string
  itineraryPdfUrl?: string
  availableDeparturesLabel?: string
  itineraryHref?: string
  href?: string
  whatsappHref?: string
  visiting?: string[]
  isVisas?: boolean
}

export default function PackageCard({
  title,
  subtitle,
  description,
  image,
  days,
  nights,
  countries,
  cities,
  priceFinal,
  currency,
  pricePrefix,
  priceSuffix,
  taxesLabel,
  includeFlight,
  airlineLogo,
  availableDeparturesLabel,
  itineraryHref,
  href = '#',
  whatsappHref = '#',
  visiting = [],
  itineraryPdfUrl,
  isVisas = false,
}: Props) {
  const { t, lang } = useI18n()
  const [openItinerary, setOpenItinerary] = useState(false)

  const fmt = (n?: number) => {
    if (typeof n !== 'number') return ''
    const cur = (currency || 'USD').toUpperCase()
    const locale = lang === 'EN' ? 'en-US' : lang === 'FR' ? 'fr-FR' : 'es-MX'
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: cur }).format(n)
    } catch {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD' }).format(n)
    }
  }

  return (
    <>
  <article className="w-[90%] mx-auto h-full flex-1 bg-white rounded-xl shadow-lg overflow-hidden flex flex-col lg:flex-row lg:justify-start gap-0 min-h-[13.2rem] ml-0">
      {/* Left image (45% on large screens) */}
      <div className="relative w-full lg:w-[45%] bg-neutral-100 min-h-[13.2rem] h-full" style={{ aspectRatio: '16 / 9' }}>
        <Image src={image} alt={title} fill className="object-cover" />
      </div>

  {/* Right details (45% on large screens) */}
  <div className="p-6 lg:w-[55%] flex flex-col justify-between min-h-[13.2rem] h-full">
        <div className="flex-1">
          <h3 className="text-2xl font-extrabold text-black" style={{ fontFamily: 'DIN, sans-serif' }}>{title}</h3>
          {subtitle && <div className="text-sm text-[#fb0009] font-semibold mt-1">{subtitle}</div>}

          {/* stats row: render only provided stats */}
          <div className="mt-4">
            {(() => {
              const stats: { label: string; value: number | undefined }[] = [
                { label: t('admin_field_days') || 'Días', value: days },
                { label: t('admin_field_nights') || 'Noches', value: nights },
                { label: t('admin_field_countries') || 'Países', value: countries },
                { label: t('admin_field_cities') || 'Ciudades', value: cities },
              ].filter(s => typeof s.value === 'number')

              if (!stats.length) return null

              return (
                <div className="grid gap-3 text-center" style={{ gridTemplateColumns: `repeat(${stats.length}, minmax(0,1fr))` }}>
                  {stats.map((s, i) => (
                    <div key={i}>
                      <div className="text-xs text-neutral-500 font-semibold">{s.label}</div>
                      <div className="font-black text-lg">{s.value}</div>
                    </div>
                  ))}
                </div>
              )
            })()}
          </div>

          {/* If this is the visas service, render a simplified block: only title and 'Ver más' button */}
          {isVisas ? (
            <div className="mt-4">
              {/* Show nothing else besides a CTA to view more */}
            </div>
          ) : (
            <>
              {/* Price / flight */}
              <div className="mt-4">
                <div className="text-sm text-neutral-500">{pricePrefix ?? (t('price_from_label') || (lang === 'FR' ? 'À partir de' : lang === 'EN' ? 'From' : 'Desde'))}</div>
                <div className="text-2xl md:text-3xl font-black text-neutral-900">{fmt(priceFinal)}{priceSuffix ? <span className="text-base font-semibold text-neutral-700 ml-2">{priceSuffix}</span> : null} {taxesLabel ? <span className="text-base font-semibold text-neutral-700">+ {taxesLabel}</span> : null}</div>
                {includeFlight && (
                  <div className="mt-3 w-full">
                    <div className="w-full flex items-center justify-center px-3 py-3">
                      {airlineLogo ? (
                        <div className="w-24 h-8 relative mr-3">
                          <Image src={airlineLogo} alt="airline" fill className="object-contain" />
                        </div>
                      ) : null}
                      <span className="text-base text-neutral-700 font-semibold text-center">{t('includes_flight') || (lang === 'FR' ? 'Inclut le vol' : lang === 'EN' ? 'Includes flight!' : '¡INCLUYE VUELO!')}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions (available departures / itinerary) */}
              
            </>
          )}
        </div>

        {/* Footer: visiting list and actions */}
        <div className="mt-4 pt-4 border-t border-neutral-100">
          {visiting && visiting.length > 0 && (
            <div className="text-sm text-neutral-700 mb-3">
              <div className="font-semibold text-neutral-600">{t('visiting') || (lang === 'FR' ? 'En visite' : lang === 'EN' ? 'Visiting' : 'Visitando')}</div>
              <div className="text-sm mt-1">{visiting.join(', ')}</div>
            </div>
          )}

                <div className="flex items-center gap-3">
            {isVisas ? (
              <a href={href} className="flex-1 bg-[#fb0009] text-white text-center py-2 rounded-lg font-bold text-base">{t('btn_see_more') || (lang === 'FR' ? 'Voir plus' : lang === 'EN' ? 'See more' : 'Ver más')}</a>
            ) : (
              <>
                <a href={href} className="flex-1 bg-[#fb0009] text-white text-center py-2 rounded-lg font-bold text-base">{t('quote_here') || (lang === 'FR' ? 'Voir les détails' : lang === 'EN' ? 'See details' : 'Ver más')}</a>
                <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-black text-white flex items-center justify-center rounded-[100px]" aria-label={t('whatsapp_title')}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="20" height="20" fill="currentColor" aria-hidden="true" focusable="false" className="inline-block">
                    <path d="M26.576 5.363c-2.69-2.69-6.406-4.354-10.511-4.354-8.209 0-14.865 6.655-14.865 14.865 0 2.732 0.737 5.291 2.022 7.491l-0.038-0.070-2.109 7.702 7.879-2.067c2.051 1.139 4.498 1.809 7.102 1.809h0.006c8.209-0.003 14.862-6.659 14.862-14.868 0-4.103-1.662-7.817-4.349-10.507l0 0zM16.062 28.228h-0.005c-0 0-0.001 0-0.001 0-2.319 0-4.489-0.64-6.342-1.753l0.056 0.031-0.451-0.267-4.675 1.227 1.247-4.559-0.294-0.467c-1.185-1.862-1.889-4.131-1.889-6.565 0-6.822 5.531-12.353 12.353-12.353s12.353 5.531 12.353 12.353c0 6.822-5.53 12.353-12.353 12.353h-0zM22.838 18.977c-0.371-0.186-2.197-1.083-2.537-1.208-0.341-0.124-0.589-0.185-0.837 0.187-0.246 0.371-0.958 1.207-1.175 1.455-0.216 0.249-0.434 0.279-0.805 0.094-1.15-0.466-2.138-1.087-2.997-1.852l0.010 0.009c-0.799-0.74-1.484-1.587-2.037-2.521l-0.028-0.052c-0.216-0.371-0.023-0.572 0.162-0.757 0.167-0.166 0.372-0.434 0.557-0.65 0.146-0.179 0.271-0.384 0.366-0.604l0.006-0.017c0.043-0.087 0.068-0.188 0.068-0.296 0-0.131-0.037-0.253-0.101-0.357l0.002 0.003c-0.094-0.186-0.836-2.014-1.145-2.758-0.302-0.724-0.609-0.625-0.836-0.637-0.216-0.010-0.464-0.012-0.712-0.012-0.395 0.010-0.746 0.188-0.988 0.463l-0.001 0.002c-0.802 0.761-1.3 1.834-1.3 3.023 0 0.026 0 0.053 0.001 0.079l-0-0.004c0.131 1.467 0.681 2.784 1.527 3.857l-0.012-0.015c1.604 2.379 3.742 4.282 6.251 5.564l0.094 0.043c0.548 0.248 1.25 0.513 1.968 0.74l0.149 0.041c0.442 0.14 0.951 0.221 1.479 0.221 0.303 0 0.601-0.027 0.889-0.078l-0.031 0.004c1.069-0.223 1.956-0.868 2.497-1.749l0.009-0.017c0.165-0.366 0.261-0.793 0.261-1.242 0-0.185-0.016-0.366-0.047-0.542l0.003 0.019c-0.092-0.155-0.34-0.247-0.712-0.434z" />
                  </svg>
                </a>
              </>
            )}
          </div>
        </div>
      </div>
    </article>
    {itineraryPdfUrl || description ? (
      <Modal open={openItinerary} onClose={() => setOpenItinerary(false)}>
        {itineraryPdfUrl ? (
          <div className="w-full h-[70vh]">
            <iframe src={itineraryPdfUrl} className="w-full h-full border-0" title="Itinerario PDF" />
          </div>
        ) : (
          <div>
            <h3 className="text-xl font-bold mb-4">{title}</h3>
            <div className="prose max-w-none text-neutral-700"><HTMLRenderer content={description || ''} /></div>
          </div>
        )}
      </Modal>
    ) : null}
    </>
  )
}
